import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Loader2, Mail, Play, Pause, Send, FileText, Clock, Languages, Database, BarChart3, Calendar, Check, X, CalendarClock, Pencil, Save, CreditCard, Activity, MapPin, MessageSquareHeart } from 'lucide-react';
import { fetchEmailTemplates, fetchCampaignSequences, sendTestCampaignEmail, seedOnboardingCampaign, seedSeasonalBroadcasts, seedWinterBroadcast, seedTesterCampaign, fetchScheduledBroadcasts, updateScheduledBroadcast, updateEmailTemplate, EmailTemplate, CampaignSequence, ScheduledBroadcast } from '../../../services/campaignService';
import AdminCampaignDashboard from './AdminCampaignDashboard';
import AdminRevenueDashboard from './AdminRevenueDashboard';
import AdminUsageDashboard from './AdminUsageDashboard';
import AdminRegionDashboard from './AdminRegionDashboard';
import AdminCheckinsDashboard from './AdminCheckinsDashboard';
import AdminOffboardingDashboard from './AdminOffboardingDashboard';

import type { Coordinates } from '../../../types';

interface Props {
  onViewSpot: (spotId: string, location: Coordinates) => void;
}

const AdminCampaignsPanel: React.FC<Props> = ({ onViewSpot }) => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignSequence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<'manage' | 'dashboard' | 'revenue' | 'usage' | 'regions' | 'checkins' | 'offboarding'>('manage');

  const [broadcasts, setBroadcasts] = useState<ScheduledBroadcast[]>([]);
  const [editingBroadcast, setEditingBroadcast] = useState<string | null>(null);
  const [editDate, setEditDate] = useState('');
  const [savingBroadcast, setSavingBroadcast] = useState<string | null>(null);

  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  const [previewLang, setPreviewLang] = useState<Record<string, string>>({});
  const [sendingTest, setSendingTest] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; ok: boolean; msg: string } | null>(null);

  // Inline template editing state
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBodyHtml, setEditBodyHtml] = useState('');
  const [editCtaLabel, setEditCtaLabel] = useState('');
  const [editCtaUrl, setEditCtaUrl] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [saveTemplateResult, setSaveTemplateResult] = useState<{ id: string; ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [tpls, seqs, bcasts] = await Promise.all([
        fetchEmailTemplates(),
        fetchCampaignSequences(),
        fetchScheduledBroadcasts()
      ]);
      setTemplates(tpls);
      setCampaigns(seqs);
      setBroadcasts(bcasts);
    } catch (e: any) {
      setError(e.message || t('admin.campaigns.error_loading'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBroadcastDate = async (broadcastId: string) => {
    if (!editDate) return;
    setSavingBroadcast(broadcastId);
    try {
      await updateScheduledBroadcast({ broadcast_id: broadcastId, send_date: editDate });
      setBroadcasts(prev => prev.map(b => b.id === broadcastId ? { ...b, send_date: editDate } : b));
      setEditingBroadcast(null);
      setEditDate('');
    } catch (e: any) {
      setError(e.message || 'Feil ved oppdatering');
    } finally {
      setSavingBroadcast(null);
    }
  };

  const handleToggleBroadcastStatus = async (broadcast: ScheduledBroadcast) => {
    const newStatus = broadcast.status === 'pending' ? 'paused' : 'pending';
    setSavingBroadcast(broadcast.id);
    try {
      await updateScheduledBroadcast({ broadcast_id: broadcast.id, status: newStatus as 'pending' | 'paused' });
      setBroadcasts(prev => prev.map(b => b.id === broadcast.id ? { ...b, status: newStatus as any } : b));
    } catch (e: any) {
      setError(e.message || 'Feil ved statusendring');
    } finally {
      setSavingBroadcast(null);
    }
  };

  const handleSendTest = async (templateId: string) => {
    setSendingTest(templateId);
    setTestResult(null);
    try {
      const lang = previewLang[templateId] || 'no';
      const result = await sendTestCampaignEmail({ template_id: templateId, language: lang });
      setTestResult({ id: templateId, ok: true, msg: t('admin.campaigns.test_sent', { email: result.sentTo }) });
    } catch (e: any) {
      setTestResult({ id: templateId, ok: false, msg: e.message || t('admin.campaigns.test_failed') });
    } finally {
      setSendingTest(null);
    }
  };

  const getLang = (id: string) => previewLang[id] || 'no';
  const toggleLang = (id: string) => {
    setPreviewLang(prev => ({ ...prev, [id]: prev[id] === 'en' ? 'no' : 'en' }));
  };

  const startEditTemplate = (tpl: EmailTemplate, lang: string) => {
    const content = tpl.content?.[lang] || tpl.content?.no;
    if (!content) return;
    setEditingTemplate(`${tpl.id}__${lang}`);
    setEditSubject(content.subject || '');
    setEditBodyHtml(content.body_html || '');
    setEditCtaLabel(content.cta_label || '');
    setEditCtaUrl(content.cta_url || '');
    setSaveTemplateResult(null);
  };

  const cancelEditTemplate = () => {
    setEditingTemplate(null);
    setEditSubject('');
    setEditBodyHtml('');
    setEditCtaLabel('');
    setEditCtaUrl('');
  };

  const handleSaveTemplate = async (tplId: string, lang: string) => {
    setSavingTemplate(true);
    setSaveTemplateResult(null);
    try {
      await updateEmailTemplate({
        template_id: tplId,
        language: lang,
        subject: editSubject,
        body_html: editBodyHtml,
        cta_label: editCtaLabel,
        cta_url: editCtaUrl,
      });
      // Update local state
      setTemplates(prev => prev.map(t => {
        if (t.id !== tplId) return t;
        return {
          ...t,
          content: {
            ...t.content,
            [lang]: { ...t.content[lang], subject: editSubject, body_html: editBodyHtml, cta_label: editCtaLabel, cta_url: editCtaUrl }
          }
        };
      }));
      setSaveTemplateResult({ id: tplId, ok: true, msg: 'Lagret!' });
      setEditingTemplate(null);
    } catch (e: any) {
      setSaveTemplateResult({ id: tplId, ok: false, msg: e.message || 'Feil ved lagring' });
    } finally {
      setSavingTemplate(false);
    }
  };

  const templateMap = new Map(templates.map(t => [t.id, t]));

  return (
    <div className="space-y-6 w-full pb-6">
      {/* Sub-tabs */}
      <div className="flex items-center gap-1.5 p-1 bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl w-fit">
        <button
          onClick={() => setActiveSubTab('manage')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${activeSubTab === 'manage'
              ? 'bg-[var(--bg-input)] text-[var(--text-primary)] shadow-xl shadow-black/20'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }`}
        >
          <Mail size={12} />
          {t('admin.campaigns.subtab_manage', 'Administrasjon')}
        </button>
        <button
          onClick={() => setActiveSubTab('dashboard')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${activeSubTab === 'dashboard'
              ? 'bg-[var(--bg-input)] text-[var(--text-primary)] shadow-xl shadow-black/20'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }`}
        >
          <BarChart3 size={12} />
          {t('admin.campaigns.subtab_dashboard', 'Dashboard')}
        </button>
        <button
          onClick={() => setActiveSubTab('revenue')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${activeSubTab === 'revenue'
              ? 'bg-[var(--bg-input)] text-[var(--text-primary)] shadow-xl shadow-black/20'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }`}
        >
          <CreditCard size={12} />
          Omsetning
        </button>
        <button
          onClick={() => setActiveSubTab('usage')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${activeSubTab === 'usage'
              ? 'bg-[var(--bg-input)] text-[var(--text-primary)] shadow-xl shadow-black/20'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }`}
        >
          <Activity size={12} />
          Bruk
        </button>
        <button
          onClick={() => setActiveSubTab('regions')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${activeSubTab === 'regions'
              ? 'bg-[var(--bg-input)] text-[var(--text-primary)] shadow-xl shadow-black/20'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }`}
        >
          <MapPin size={12} />
          Regioner
        </button>
        <button
          onClick={() => setActiveSubTab('checkins')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${activeSubTab === 'checkins'
              ? 'bg-[var(--bg-input)] text-[var(--text-primary)] shadow-xl shadow-black/20'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }`}
        >
          <Check size={12} />
          Innsjekkinger
        </button>
        <button
          onClick={() => setActiveSubTab('offboarding')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${activeSubTab === 'offboarding'
              ? 'bg-[var(--bg-input)] text-[var(--text-primary)] shadow-xl shadow-black/20'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }`}
        >
          <MessageSquareHeart size={12} />
          Tilbakemeldinger
        </button>
      </div>

      {activeSubTab === 'dashboard' ? (
        <AdminCampaignDashboard />
      ) : activeSubTab === 'revenue' ? (
        <AdminRevenueDashboard />
      ) : activeSubTab === 'usage' ? (
        <AdminUsageDashboard />
      ) : activeSubTab === 'regions' ? (
        <AdminRegionDashboard />
      ) : activeSubTab === 'checkins' ? (
        <AdminCheckinsDashboard onViewSpot={onViewSpot} />
      ) : activeSubTab === 'offboarding' ? (
        <AdminOffboardingDashboard />
      ) : loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={20} className="animate-spin text-[var(--text-secondary)]" />
        </div>
      ) : error ? (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
          <p className="text-tiny text-red-400">{error}</p>
        </div>
      ) : (
        <>
          {/* Templates section */}
          <div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-3">
              <FileText size={11} className="inline mr-1.5 -mt-0.5" />
              {t('admin.campaigns.templates_title')}
            </p>

            {templates.length === 0 ? (
              <p className="text-tiny text-[var(--text-muted)]">{t('admin.campaigns.no_templates')}</p>
            ) : (
              <div className="space-y-2">
                {templates.map(tpl => {
                  const expanded = expandedTemplate === tpl.id;
                  const lang = getLang(tpl.id);
                  const content = tpl.content?.[lang] || tpl.content?.no;

                  return (
                    <div key={tpl.id} className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl overflow-hidden">
                      <button
                        onClick={() => setExpandedTemplate(expanded ? null : tpl.id)}
                        className="w-full flex items-center gap-3 p-4 text-left hover:bg-[var(--bg-surface-alt)] transition-colors"
                      >
                        {expanded ? <ChevronDown size={14} className="text-[var(--text-secondary)] shrink-0" /> : <ChevronRight size={14} className="text-[var(--text-secondary)] shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-[var(--text-primary)] truncate">{tpl.name}</p>
                          <p className="text-2xs text-[var(--text-muted)] truncate">{tpl.description}</p>
                        </div>
                        <span className={`text-3xs font-black uppercase tracking-widest px-2 py-1 rounded-lg ${tpl.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-stone-500/10 text-[var(--text-secondary)]'}`}>
                          {tpl.status}
                        </span>
                      </button>

                      {expanded && content && (() => {
                        const isEditing = editingTemplate === `${tpl.id}__${lang}`;
                        return (
                        <div className="px-4 pb-4 space-y-3 border-t border-[var(--border-primary)] pt-3">
                          {/* Language toggle + Edit button */}
                          <div className="flex items-center gap-2">
                            <Languages size={12} className="text-[var(--text-muted)]" />
                            <button
                              onClick={() => { if (isEditing) cancelEditTemplate(); toggleLang(tpl.id); }}
                              className="text-3xs font-black uppercase tracking-widest px-2.5 py-1 rounded-lg bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:bg-violet-500/20 transition-colors"
                            >
                              {lang === 'no' ? 'Norsk' : 'English'} &rarr; {lang === 'no' ? 'EN' : 'NO'}
                            </button>
                            <div className="flex-1" />
                            {!isEditing ? (
                              <button
                                onClick={() => startEditTemplate(tpl, lang)}
                                className="flex items-center gap-1.5 text-3xs font-black uppercase tracking-widest px-2.5 py-1 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-colors"
                              >
                                <Pencil size={10} />
                                Rediger
                              </button>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={() => handleSaveTemplate(tpl.id, lang)}
                                  disabled={savingTemplate}
                                  className="flex items-center gap-1.5 text-3xs font-black uppercase tracking-widest px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
                                >
                                  {savingTemplate ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />}
                                  Lagre
                                </button>
                                <button
                                  onClick={cancelEditTemplate}
                                  className="flex items-center gap-1.5 text-3xs font-black uppercase tracking-widest px-2.5 py-1 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
                                >
                                  <X size={10} />
                                  Avbryt
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Subject */}
                          <div>
                            <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1">{t('admin.campaigns.subject')}</p>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editSubject}
                                onChange={e => setEditSubject(e.target.value)}
                                className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] font-mono focus:outline-none focus:border-violet-500/50"
                              />
                            ) : (
                              <p className="text-xs text-[var(--text-secondary)] bg-[var(--bg-input)] px-3 py-2 rounded-lg font-mono">{content.subject}</p>
                            )}
                          </div>

                          {/* Hero image */}
                          {tpl.header_image_url && (
                            <div>
                              <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1">Hero image</p>
                              <img src={tpl.header_image_url} alt="" className="w-full max-h-32 object-cover rounded-lg border border-[var(--border-secondary)]" />
                            </div>
                          )}

                          {/* Body preview / editor */}
                          <div>
                            <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1">{t('admin.campaigns.body_preview')}</p>
                            {isEditing ? (
                              <textarea
                                value={editBodyHtml}
                                onChange={e => setEditBodyHtml(e.target.value)}
                                rows={12}
                                className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] font-mono leading-relaxed focus:outline-none focus:border-violet-500/50 resize-y"
                              />
                            ) : (
                              <div
                                className="text-xs text-[var(--text-secondary)] bg-[var(--bg-input)] px-3 py-2 rounded-lg leading-relaxed max-h-48 overflow-y-auto"
                                dangerouslySetInnerHTML={{ __html: content.body_html }}
                              />
                            )}
                          </div>

                          {/* CTA */}
                          {isEditing ? (
                            <div className="space-y-2">
                              <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">CTA</p>
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={editCtaLabel}
                                  onChange={e => setEditCtaLabel(e.target.value)}
                                  placeholder="Knappetekst"
                                  className="flex-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-violet-500/50"
                                />
                                <input
                                  type="text"
                                  value={editCtaUrl}
                                  onChange={e => setEditCtaUrl(e.target.value)}
                                  placeholder="URL"
                                  className="flex-[2] bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg px-3 py-2 text-xs text-[var(--text-secondary)] font-mono focus:outline-none focus:border-violet-500/50"
                                />
                              </div>
                            </div>
                          ) : content.cta_label ? (
                            <div className="flex items-center gap-2 text-2xs">
                              <span className="text-[var(--text-muted)] font-bold">CTA:</span>
                              <span className="text-emerald-400 font-bold">{content.cta_label}</span>
                              <span className="text-[var(--text-muted)]">&rarr;</span>
                              <span className="text-[var(--text-secondary)] truncate">{content.cta_url}</span>
                            </div>
                          ) : null}

                          {/* Save result feedback */}
                          {saveTemplateResult?.id === tpl.id && (
                            <p className={`text-2xs ${saveTemplateResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>
                              {saveTemplateResult.msg}
                            </p>
                          )}

                          {/* Variables */}
                          {tpl.variables?.length > 0 && (
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-3xs text-[var(--text-muted)] font-bold">{t('admin.campaigns.variables')}:</span>
                              {tpl.variables.map(v => (
                                <span key={v} className="text-3xs px-1.5 py-0.5 bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded text-[var(--text-secondary)] font-mono">
                                  {'{{' + v + '}}'}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Test send */}
                          <div className="flex items-center gap-2 pt-1">
                            <button
                              onClick={() => handleSendTest(tpl.id)}
                              disabled={sendingTest === tpl.id}
                              className="flex items-center gap-2 px-4 py-2 bg-violet-600/20 hover:bg-violet-600/30 border border-violet-500/30 text-violet-300 rounded-xl text-2xs font-black uppercase tracking-widest disabled:opacity-50 transition-all"
                            >
                              {sendingTest === tpl.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                              {t('admin.campaigns.send_test')}
                            </button>
                            {testResult?.id === tpl.id && (
                              <span className={`text-2xs ${testResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>
                                {testResult.msg}
                              </span>
                            )}
                          </div>
                        </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Campaigns section */}
          <div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-3">
              <Mail size={11} className="inline mr-1.5 -mt-0.5" />
              {t('admin.campaigns.sequences_title')}
            </p>

            {campaigns.length === 0 ? (
              <p className="text-tiny text-[var(--text-muted)]">{t('admin.campaigns.no_sequences')}</p>
            ) : (
              <div className="space-y-2">
                {campaigns.map(seq => (
                  <div key={seq.id} className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[var(--text-primary)]">{seq.name}</p>
                        <p className="text-2xs text-[var(--text-muted)]">
                          {seq.steps?.length || 0} {t('admin.campaigns.steps_count')}
                          {seq.require_newsletter_consent && ` · ${t('admin.campaigns.requires_consent')}`}
                        </p>
                      </div>
                      <span className={`flex items-center gap-1.5 text-3xs font-black uppercase tracking-widest px-2.5 py-1 rounded-lg ${seq.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' :
                          seq.status === 'paused' ? 'bg-amber-500/10 text-amber-400' :
                            'bg-stone-500/10 text-[var(--text-secondary)]'
                        }`}>
                        {seq.status === 'active' ? <Play size={9} /> : seq.status === 'paused' ? <Pause size={9} /> : null}
                        {seq.status}
                      </span>
                    </div>

                    {/* Steps */}
                    {seq.steps?.length > 0 && (
                      <div className="space-y-1">
                        {seq.steps.map(step => {
                          const tpl = templateMap.get(step.template_id);
                          return (
                            <div key={step.step_number} className={`flex items-center gap-3 px-3 py-2 rounded-lg text-tiny ${step.enabled !== false ? 'bg-[var(--bg-input)]' : 'bg-black/10 opacity-50'}`}>
                              <span className="text-[var(--text-muted)] font-mono w-5 text-center">{step.step_number}</span>
                              <Clock size={10} className="text-[var(--text-muted)] shrink-0" />
                              <span className="text-[var(--text-secondary)]">
                                {step.delay_days === 0 ? t('admin.campaigns.day_zero') : t('admin.campaigns.day_n', { days: step.delay_days })}
                              </span>
                              <span className="text-[var(--text-secondary)] font-bold truncate flex-1">
                                {tpl?.name || step.template_id}
                              </span>
                              {step.enabled === false && (
                                <span className="text-4xs text-[var(--text-muted)] uppercase tracking-widest">{t('admin.campaigns.disabled')}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Scheduled Broadcasts section */}
          {broadcasts.length > 0 && (
            <div>
              <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-3">
                <CalendarClock size={11} className="inline mr-1.5 -mt-0.5" />
                Planlagte Broadcasts
              </p>

              <div className="space-y-2">
                {broadcasts.map(bc => {
                  const isEditing = editingBroadcast === bc.id;
                  const isSaving = savingBroadcast === bc.id;
                  const isPast = bc.send_date < new Date().toISOString().slice(0, 10);
                  const tpl = templates.find(t => t.id === bc.template_id);

                  const statusColor = bc.status === 'sent' ? 'bg-emerald-500/10 text-emerald-400'
                    : bc.status === 'sending' ? 'bg-blue-500/10 text-blue-400'
                    : bc.status === 'error' ? 'bg-red-500/10 text-red-400'
                    : bc.status === 'paused' ? 'bg-amber-500/10 text-amber-400'
                    : 'bg-stone-500/10 text-[var(--text-secondary)]';

                  const statusIcon = bc.status === 'sent' ? <Check size={9} />
                    : bc.status === 'paused' ? <Pause size={9} />
                    : bc.status === 'sending' ? <Loader2 size={9} className="animate-spin" />
                    : <Clock size={9} />;

                  return (
                    <div key={bc.id} className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl p-4 space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-[var(--text-primary)] truncate">{bc.name}</p>
                          <p className="text-2xs text-[var(--text-muted)] truncate">
                            {tpl?.name || bc.template_id}
                            {bc.target_audience !== 'all' && ` · ${bc.target_audience}`}
                          </p>
                        </div>
                        <span className={`flex items-center gap-1.5 text-3xs font-black uppercase tracking-widest px-2.5 py-1 rounded-lg ${statusColor}`}>
                          {statusIcon}
                          {bc.status === 'paused' ? t('admin.campaigns.paused') : bc.status}
                        </span>
                      </div>

                      {/* Date row */}
                      <div className="flex items-center gap-2">
                        <Calendar size={12} className="text-[var(--text-muted)] shrink-0" />
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="date"
                              value={editDate}
                              onChange={e => setEditDate(e.target.value)}
                              className="bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg px-2.5 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-violet-500/50"
                            />
                            <button
                              onClick={() => handleSaveBroadcastDate(bc.id)}
                              disabled={isSaving}
                              className="p-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors disabled:opacity-50"
                            >
                              {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                            </button>
                            <button
                              onClick={() => { setEditingBroadcast(null); setEditDate(''); }}
                              className="p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-mono ${isPast ? 'text-[var(--text-muted)]' : 'text-[var(--text-secondary)]'}`}>
                              {bc.send_date}
                            </span>
                            {(bc.status === 'pending' || bc.status === 'paused') && (
                              <button
                                onClick={() => { setEditingBroadcast(bc.id); setEditDate(bc.send_date); }}
                                className="text-3xs font-black uppercase tracking-widest px-2 py-0.5 rounded-lg bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:bg-violet-500/20 transition-colors"
                              >
                                Endre dato
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Sent stats */}
                      {(bc.sent_count != null || bc.error_count != null) && (
                        <div className="flex items-center gap-3 text-2xs text-[var(--text-muted)]">
                          {bc.sent_count != null && <span>Sendt: <span className="text-emerald-400 font-bold">{bc.sent_count}</span></span>}
                          {bc.error_count != null && bc.error_count > 0 && <span>Feil: <span className="text-red-400 font-bold">{bc.error_count}</span></span>}
                        </div>
                      )}

                      {/* Pause/Resume button */}
                      {(bc.status === 'pending' || bc.status === 'paused') && (
                        <button
                          onClick={() => handleToggleBroadcastStatus(bc)}
                          disabled={isSaving}
                          className={`flex items-center gap-1.5 text-3xs font-black uppercase tracking-widest px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 ${
                            bc.status === 'pending'
                              ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20'
                              : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20'
                          }`}
                        >
                          {bc.status === 'pending' ? <><Pause size={10} /> {t('admin.campaigns.pause')}</> : <><Play size={10} /> {t('admin.campaigns.activate')}</>}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Seed button */}
          <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl p-4 space-y-3">
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">
              <Database size={11} className="inline mr-1.5 -mt-0.5" />
              {t('admin.campaigns.seed_title')}
            </p>
            <p className="text-2xs text-[var(--text-muted)]">{t('admin.campaigns.seed_description')}</p>
            <button
              onClick={async () => {
                setSeeding(true);
                setSeedResult(null);
                try {
                  const res = await seedOnboardingCampaign();
                  setSeedResult(t('admin.campaigns.seed_success', { count: res.templates.length, route: res.featuredRoute?.name || '-' }));
                  loadData();
                } catch (e: any) {
                  setSeedResult(e.message || t('admin.campaigns.seed_failed'));
                } finally {
                  setSeeding(false);
                }
              }}
              disabled={seeding}
              className="flex items-center gap-2 px-4 py-2.5 bg-violet-600/20 hover:bg-violet-600/30 border border-violet-500/30 text-violet-300 rounded-xl text-2xs font-black uppercase tracking-widest disabled:opacity-50 transition-all"
            >
              {seeding ? <Loader2 size={12} className="animate-spin" /> : <Database size={12} />}
              {t('admin.campaigns.seed_button')}
            </button>
            {seedResult && <p className="text-2xs text-[var(--text-secondary)]">{seedResult}</p>}
          </div>

          {/* Seed Seasonal Broadcasts */}
          <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl p-4 space-y-3">
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">
              <Mail size={11} className="inline mr-1.5 -mt-0.5" />
              Sesongkampanjer (Broadcasts)
            </p>
            <p className="text-2xs text-[var(--text-muted)]">Opprett 3 sesongmaler (vår, sommer, høst) og planlagte broadcasts med smarte datoer.</p>
            <button
              onClick={async () => {
                setSeeding(true);
                setSeedResult(null);
                try {
                  const res = await seedSeasonalBroadcasts();
                  setSeedResult(`Sesongkampanjer opprettet: ${res.templates.length} maler, ${res.broadcasts.length} broadcasts`);
                  loadData();
                } catch (e: any) {
                  setSeedResult(e.message || 'Feil ved opprettelse av sesongkampanjer');
                } finally {
                  setSeeding(false);
                }
              }}
              disabled={seeding}
              className="flex items-center gap-2 px-4 py-2.5 bg-amber-600/20 hover:bg-amber-600/30 border border-amber-500/30 text-amber-300 rounded-xl text-2xs font-black uppercase tracking-widest disabled:opacity-50 transition-all"
            >
              {seeding ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
              Seed sesongkampanjer
            </button>
          </div>

          {/* Seed Winter Broadcast */}
          <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl p-4 space-y-3">
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">
              <Mail size={11} className="inline mr-1.5 -mt-0.5" />
              Vinterkampanje (Broadcast)
            </p>
            <p className="text-2xs text-[var(--text-muted)]">Opprett vintermal og planlagt broadcast for 1. november (snødybde, vinterstengte veier, korte dager).</p>
            <button
              onClick={async () => {
                setSeeding(true);
                setSeedResult(null);
                try {
                  const res = await seedWinterBroadcast();
                  const tParts = [];
                  if (res.template_created) tParts.push('mal opprettet');
                  else tParts.push('mal eksisterte allerede');
                  if (res.broadcast.created) tParts.push(`broadcast for ${res.broadcast.date} opprettet`);
                  else tParts.push('broadcast eksisterte allerede');
                  setSeedResult(`Vinterkampanje: ${tParts.join(', ')}`);
                  loadData();
                } catch (e: any) {
                  setSeedResult(e.message || 'Feil ved opprettelse av vinterkampanje');
                } finally {
                  setSeeding(false);
                }
              }}
              disabled={seeding}
              className="flex items-center gap-2 px-4 py-2.5 bg-sky-600/20 hover:bg-sky-600/30 border border-sky-500/30 text-sky-300 rounded-xl text-2xs font-black uppercase tracking-widest disabled:opacity-50 transition-all"
            >
              {seeding ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
              Seed vinterkampanje
            </button>
          </div>

          {/* Seed Tester Campaign */}
          <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl p-4 space-y-3">
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">
              <Send size={11} className="inline mr-1.5 -mt-0.5" />
              Tester Onboarding (Drip)
            </p>
            <p className="text-2xs text-[var(--text-muted)]">Opprett 4 tester-maler (velkommen, kart-QA, ruter, Android) og drip-kampanje kun for beta-testere.</p>
            <button
              onClick={async () => {
                setSeeding(true);
                setSeedResult(null);
                try {
                  const res = await seedTesterCampaign();
                  setSeedResult(`Tester-kampanje opprettet: ${res.templates.length} maler, kampanje: ${res.campaign}`);
                  loadData();
                } catch (e: any) {
                  setSeedResult(e.message || 'Feil ved opprettelse av tester-kampanje');
                } finally {
                  setSeeding(false);
                }
              }}
              disabled={seeding}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-300 rounded-xl text-2xs font-black uppercase tracking-widest disabled:opacity-50 transition-all"
            >
              {seeding ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Seed tester-kampanje
            </button>
          </div>

          {/* Info box */}
          <div className="bg-violet-500/5 border border-violet-500/15 rounded-2xl p-4">
            <p className="text-2xs text-violet-300/80 leading-relaxed">
              {t('admin.campaigns.info_text')}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminCampaignsPanel;
